import { z } from 'zod';

/**
 * Wave 18 — Fluxo de Caixa Projetado (per project).
 *
 * A monthly forecast that combines:
 *
 *   - Realized inflow  (settled positive payments, bucketed by settled_at)
 *   - Realized outflow (settled negative payments, bucketed by settled_at)
 *   - Projected inflow (measurements approved/sent but not yet paid,
 *                       bucketed at period_end + 30d)
 *   - Projected outflow (open POs still owed, bucketed at
 *                        expected_delivery_at, fallback issued_at + 30d)
 *
 * The series is always exactly `months` buckets long, starting from
 * (today - monthsPast months) and going forward. Buckets the caller
 * cares about are addressable by their YYYY-MM key so the web can
 * highlight the current month.
 */

export const fluxoCaixaBucketSchema = z.object({
  /** YYYY-MM (month key, tz-UTC start of month). */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  /** Inflow already collected — positive settled payments. */
  realizedInflowCents: z.number().int(),
  /** Outflow already paid — negative settled payments (sign-flipped to positive for display). */
  realizedOutflowCents: z.number().int(),
  /** Expected inflow — measurements in approved/sent awaiting payment. */
  projectedInflowCents: z.number().int(),
  /** Expected outflow — open POs not yet settled. */
  projectedOutflowCents: z.number().int(),
  /** realized + projected net for the month (inflow - outflow). */
  netCents: z.number().int(),
  /** Running total of net through this month (starts from openingBalance). */
  cumulativeCents: z.number().int(),
  /** True when this bucket straddles today's month. */
  isCurrent: z.boolean(),
});
export type FluxoCaixaBucket = z.infer<typeof fluxoCaixaBucketSchema>;

export const fluxoCaixaSummarySchema = z.object({
  projectId: z.string(),
  /** Cash position carried in at the start of the first bucket (0 for now — seed for future Wave 18.1). */
  openingBalanceCents: z.number().int(),
  buckets: z.array(fluxoCaixaBucketSchema),
  /** Totals across the full window. */
  totals: z.object({
    realizedInflowCents: z.number().int(),
    realizedOutflowCents: z.number().int(),
    projectedInflowCents: z.number().int(),
    projectedOutflowCents: z.number().int(),
    netCents: z.number().int(),
  }),
  /** Current-month key — the UI highlights this bucket. */
  currentMonth: z.string().regex(/^\d{4}-\d{2}$/),
  generatedAt: z.string(),
});
export type FluxoCaixaSummary = z.infer<typeof fluxoCaixaSummarySchema>;
